import {
  AccessTime,
  Add,
  ArrowForwardOutlined,
  Clear,
  CreateOutlined,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Person,
  PersonAddAltOutlined,
  PowerSettingsNewOutlined,
  Search,
  ShieldOutlined,
  Sort,
} from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { isEmpty, map, size } from 'lodash'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'
import {
  createUser,
  deleteUser,
  FieldValidationError,
  getRoleChangeRequestOptions,
  getUserFilterOptions,
  listUserHistory,
  listUsers,
  updateUser,
  User,
  UserCreatePayload,
  UserFilterOptions,
  UserHistoryParams,
  UserLog,
  UserUpdatePayload,
} from '../../api'
import DeleteIcon from '../../assets/delete_red.svg'
import EmptyBox from '../../assets/empty_box.svg'
import FetchError from '../../assets/fetch_error.svg'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import { AppStateContext } from '../../state/AppProvider'
import { getRoleLabel } from '../../utils/permissions'
import CreateUserModal from './CreateUserModal/CreateUserModal'
import FiltersDialog from './FiltersDialog/FiltersDialog'
import styles from './UsersRolesAndPermissionsManagement.module.css'

const PAGE_SIZE = 15
const UsersRolesAndPermissionsManagement = () => {
  const appStateContext = useContext(AppStateContext)
  const [currentTab, setCurrentTab] = useState<'users' | 'history'>('users')
  const [search, setSearch] = useState<string>(appStateContext?.state.userFilter?.search ?? '')
  const [historyFilters, setHistoryFilters] = useState<UserHistoryParams>(
    appStateContext?.state.userHistoryFilter ?? {
      search: '',
      action_type: '',
      start_date: '',
      end_date: '',
      processed_by_user_id: '',
    },
  )
  const [filterOptions, setFilterOptions] = useState<UserFilterOptions | null>(null)
  const [selectedRoles, setSelectedRoles] = useState<string[]>(appStateContext?.state.userFilter?.role ?? [])
  const [users, setUsers] = useState<User[]>([])
  const [historyRecords, setHistoryRecords] = useState<UserLog[]>([])
  const [historyTotal, setHistoryTotal] = useState<number>(0)
  const [historyPage, setHistoryPage] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [hasFetchError, setHasFetchError] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [debouncedSearch] = useDebounce(search, 500)
  const [debouncedHistorySearch] = useDebounce(historyFilters.search ?? '', 500)
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState<boolean>(false)
  const [ouOptions, setOuOptions] = useState<string[]>([])
  const [errors, setErrors] = useState<FieldValidationError[]>([])
  const [isFiltersDialogOpen, setIsFiltersDialogOpen] = useState<boolean>(false)
  const isEditingUser = useMemo(() => {
    return !isEmpty(selectedUser)
  }, [selectedUser])

  const fetchOuOptions = async () => {
    const payload = await getRoleChangeRequestOptions()
    setOuOptions(payload?.options?.ou ?? [])
  }

  const getUsersCount = useMemo(() => {
    return {
      start: page * PAGE_SIZE - PAGE_SIZE + 1,
      end: Math.min(page * PAGE_SIZE, total),
      total: total,
    }
  }, [page, total])

  const getHistoryCount = useMemo(() => {
    return {
      start: historyPage * PAGE_SIZE - PAGE_SIZE + 1,
      end: Math.min(historyPage * PAGE_SIZE, historyTotal),
      total: historyTotal,
    }
  }, [historyPage, historyTotal])

  const getLastPage = useCallback(() => {
    return Math.ceil(total / PAGE_SIZE)
  }, [total])

  const getHistoryLastPage = useCallback(() => {
    return Math.ceil(historyTotal / PAGE_SIZE)
  }, [historyTotal])

  const fetchHistory = (page: number, filters: UserHistoryParams) => {
    listUserHistory({
      page,
      page_size: PAGE_SIZE,
      ...filters,
    })
      .then(res => {
        if (res) {
          setHistoryRecords(res.logs ?? [])
          setHistoryTotal(res.total ?? 0)
          setHistoryPage(page)
          setHasFetchError(false)
          setHistoryFilters(filters)
          appStateContext?.dispatch({
            type: 'SET_USER_HISTORY_FILTER',
            payload: filters,
          })
        } else {
          setHasFetchError(true)
        }
      })
      .catch(() => {
        setHasFetchError(true)
      })
  }

  const fetchUsers = (page: number, filters: { role: string[]; search: string } = { role: [], search: '' }) => {
    listUsers({
      page,
      page_size: PAGE_SIZE,
      search: filters.search,
      filters: { role: filters.role },
    })
      .then(res => {
        if (res) {
          setUsers(res.users ?? [])
          setTotal(res.total ?? 0)
          setPage(page)
          setHasFetchError(false)
          appStateContext?.dispatch({
            type: 'SET_USER_FILTER',
            payload: { role: filters.role, search: filters.search },
          })
        } else {
          setHasFetchError(true)
        }
      })
      .catch(() => {
        setHasFetchError(true)
      })
  }

  useEffect(() => {
    getUserFilterOptions().then(options => {
      setFilterOptions(options)
    })
    fetchOuOptions()
    if (currentTab === 'history') {
      fetchHistory(1, historyFilters)
    } else {
      fetchUsers(1)
    }
  }, [])

  useEffect(() => {
    if (currentTab === 'history') {
      fetchHistory(1, historyFilters)
    } else {
      fetchUsers(1)
    }
  }, [currentTab])

  useEffect(() => {
    if (currentTab === 'users' && isEmpty(users) && page > 1) {
      fetchUsers(page - 1, {
        role: selectedRoles,
        search: debouncedSearch,
      })
    }
  }, [users, currentTab])

  useEffect(() => {
    fetchUsers(page, {
      role: selectedRoles,
      search: debouncedSearch,
    })
  }, [selectedRoles])

  useEffect(() => {
    if (currentTab === 'users' && (size(debouncedSearch) >= 2 || isEmpty(debouncedSearch))) {
      fetchUsers(page, {
        role: selectedRoles,
        search: debouncedSearch,
      })
    }
  }, [debouncedSearch])

  useEffect(() => {
    if (currentTab === 'history' && (size(debouncedHistorySearch) >= 2 || isEmpty(debouncedHistorySearch))) {
      fetchHistory(historyPage, {
        search: debouncedHistorySearch,
        action_type: historyFilters.action_type,
        start_date: historyFilters.start_date,
        end_date: historyFilters.end_date,
        processed_by_user_id: historyFilters.processed_by_user_id,
      })
    }
  }, [debouncedHistorySearch])

  const getActiveFiltersCount = () => {
    return Object.entries(historyFilters || {}).filter(
      ([key, value]) => key !== 'search' && value !== undefined && value !== '' && value !== null,
    ).length
  }

  const getRoleClassName = (role: string) => {
    switch (role) {
      case 'admin':
        return styles.userInfoRoleChipAdmin
      case 'decoder':
        return styles.userInfoRoleChipDecoder
      case 'ou_orchestrator':
        return styles.userInfoRoleChipOuOrchestrator
      case 'editor':
        return styles.userInfoRoleChipEditor
      case 'viewer':
        return styles.userInfoRoleChipViewer
      default:
        return undefined
    }
  }

  const getUserInitials = (user: User) => {
    if (!user.name) return user.email.charAt(0).toUpperCase()
    const names = user.name.trim().split(' ')
    if (names.length === 1) return names[0].charAt(0).toUpperCase()
    return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase()
  }

  const handleUserChange = (data: UserUpdatePayload) => {
    updateUser(selectedUser?.id as string, {
      organization_name: data.organization_name,
      function: data.function,
      ou: data.ou,
      role: data.role,
    })
      .then(res => {
        if (res.success) {
          setUsers(prev => prev.map(u => (u.id === selectedUser?.id ? (res.user as User) : u)))
          setIsCreateUserModalOpen(false)
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Changes saved succesfully.', severity: 'success' },
          })
          setSelectedUser(null)
        } else {
          setIsCreateUserModalOpen(false)
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Failed on saving changes. Try again later.', severity: 'warning' },
          })
          setSelectedUser(null)
        }
      })
      .catch(err => {
        setIsCreateUserModalOpen(false)
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Failed on saving changes. Try again later.', severity: 'warning' },
        })
        setSelectedUser(null)
      })
  }

  const handleCreateUser = ({
    name,
    email,
    organization_name,
    function: functionField,
    ou,
    role,
  }: UserCreatePayload) => {
    createUser({
      name: name,
      email: email,
      organization_name: organization_name,
      function: functionField,
      ou: ou,
      role: role,
    })
      .then(res => {
        if (res.success) {
          setIsCreateUserModalOpen(false)
          fetchUsers(page, {
            role: selectedRoles,
            search: debouncedSearch,
          })
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'New user added succesfully.', severity: 'success' },
          })
        } else {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'User adding failed. Try again later.', severity: 'warning' },
          })
          setErrors(res.errors ?? [])
        }
      })
      .catch(err => {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'User adding failed. Try again later.', severity: 'warning' },
        })
      })
  }

  const handleDeleteUser = () => {
    deleteUser(selectedUser?.id as string)
      .then(res => {
        if (res.success) {
          setSelectedUser(null)
          setIsDeleteModalOpen(false)
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Deletion made succesfully.', severity: 'success' },
          })
          fetchUsers(page, {
            role: selectedRoles,
            search: debouncedSearch,
          })
        } else {
          appStateContext?.dispatch({
            type: 'SET_SNACKBAR_STATE',
            payload: { message: 'Deletion failed. Try again later.', severity: 'warning' },
          })
        }
      })
      .catch(err => {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Deletion failed. Try again later.', severity: 'warning' },
        })
      })
  }
  const usersCard = (user: User) => {
    return (
      <div
        className={styles.usersCard}
        key={user.id}>
        <div className={styles.userInfoSection}>
          <div className={styles.userInfoIcon}>{getUserInitials(user)}</div>
          <div className={styles.userInfo}>
            <div className={styles.userInfoNameAndEmail}>
              <p className={styles.userInfoName}>{user.name}</p>
              <p className={styles.userInfoEmail}>{user.email}</p>
            </div>
            <div className={styles.userInfoRoleAndOu}>
              {user.role && (
                <Chip
                  label={getRoleLabel(user.role)}
                  className={getRoleClassName(user.role)}
                />
              )}
              {user.ou && !isEmpty(user.ou) && (
                <Chip
                  label={user.ou}
                  className={styles.userInfoOuChip}
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.userActionsSection}>
          <IconButton
            onClick={() => {
              setSelectedUser(user)
              setIsCreateUserModalOpen(true)
            }}>
            <CreateOutlined className={styles.createIcon} />
          </IconButton>
          <IconButton
            className={styles.deleteIconButton}
            onClick={() => {
              setSelectedUser(user)
              setIsDeleteModalOpen(true)
            }}>
            <img src={DeleteIcon} />
          </IconButton>
        </div>
      </div>
    )
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'add_user':
        return <PersonAddAltOutlined />
      case 'change_role':
        return <ShieldOutlined />
      case 'delete_user':
        return <PowerSettingsNewOutlined />
      default:
        return null
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    return dayjs.utc(dateString).local().format('MM/DD/YYYY, HH:mm:ss A')
  }

  const getHistoryInfoText = (history: UserLog) => {
    switch (history.action_type) {
      case 'add_user':
        return `Added new user with ${getRoleLabel(history.changes?.role.new)} role`
      case 'change_role':
        return `Changed role from ${getRoleLabel(history.changes?.role.old)} to ${getRoleLabel(history.changes?.role.new)}`
      case 'delete_user':
        return `Deleted`
      default:
        return ''
    }
  }
  const historyCard = (history: UserLog) => {
    return (
      <div
        className={styles.historyCard}
        key={history.id}>
        <div className={styles.actionIcon}>{getActionIcon(history.action_type)}</div>
        <div className={styles.historyInfo}>
          <div className={styles.historyInfoHeader}>
            <p className={styles.historyInfoText}>{getHistoryInfoText(history)}</p>
            <p className={styles.historyInfoDate}>{formatDate(history.created_at)}</p>
          </div>
          <div className={styles.historyInfoContent}>
            <p className={styles.historyInfoContentName}>{history.target.name}</p>
            <p className={styles.historyInfoContentEmail}>{history.target.email}</p>
            {history.action_type !== 'delete_user' && (
              <div className={styles.historyInfoContentRole}>
                {history.changes?.role && (
                  <Chip
                    label={getRoleLabel(history.changes?.role.old)}
                    className={getRoleClassName(history.changes?.role.old)}
                  />
                )}
                {history.action_type === 'change_role' && (
                  <>
                    <ArrowForwardOutlined className={styles.historyInfoContentArrow} />
                    <Chip
                      label={getRoleLabel(history.changes?.role.new)}
                      className={getRoleClassName(history.changes?.role.new)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          <div className={styles.historyInfoFooter}>
            <p className={styles.historyInfoFooterText}>Processed by:</p>
            <p className={styles.historyInfoFooterValue}>{history.processed_by.name}</p>
            <p className={styles.historyInfoFooterDot}>•</p>
            <p className={styles.historyInfoFooterEmail}>{history.processed_by.email}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.headerText}>Users Roles and Permissions Management</h1>
        <p className={styles.headerDescription}>Manage users, roles and permissions for your platform</p>
      </div>
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          className={styles.tabContainer}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue: 'users' | 'history') => setCurrentTab(newValue)}
            classes={{ indicator: styles.tabIndicator }}>
            <Tab
              className={styles.tab}
              icon={<Person />}
              iconPosition="start"
              label="Users"
              value="users"
            />
            <Tab
              className={styles.tab}
              icon={<AccessTime />}
              iconPosition="start"
              label="History"
              value="history"
            />
          </Tabs>
        </Box>
      </Box>
      <div className={styles.contentHeader}>
        <p className={styles.contentHeaderText}>{currentTab === 'users' ? 'Manage users' : 'Changes history'}</p>
        <div className={styles.contentHeaderActions}>
          <FormControl>
            <TextField
              size="small"
              variant="outlined"
              classes={{
                root: styles.searchInput,
              }}
              slotProps={{
                input: {
                  startAdornment: <Search className={styles.searchIcon} />,
                  endAdornment: !isEmpty(search) ? (
                    <Clear
                      className={styles.clearIcon}
                      onClick={() => {
                        currentTab === 'users' ? setSearch('') : setHistoryFilters({ ...historyFilters, search: '' })
                      }}
                    />
                  ) : null,
                },
              }}
              value={currentTab === 'users' ? search : historyFilters.search}
              onChange={e =>
                currentTab === 'users'
                  ? setSearch(e.target.value)
                  : setHistoryFilters({ ...historyFilters, search: e.target.value })
              }
              placeholder="Search ..."
            />
          </FormControl>
          {currentTab === 'users' ? (
            <>
              <FormControl
                size="small"
                className={styles.roleSelect}>
                {isEmpty(selectedRoles) && <InputLabel shrink={false}>All</InputLabel>}
                <Select<string[]>
                  multiple
                  size="small"
                  value={selectedRoles}
                  renderValue={selected => selected.map(getRoleLabel).join(', ')}
                  onChange={(e: SelectChangeEvent<string[]>) => {
                    setSelectedRoles(e.target.value as string[])
                  }}>
                  {map(filterOptions?.roles, option => (
                    <MenuItem
                      key={option.value}
                      value={option.value}>
                      <Checkbox
                        checked={selectedRoles.includes(option.value)}
                        classes={{ checked: styles.menuItemSelectedCheckbox }}
                      />
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Add />}
                className={styles.newUserButton}
                onClick={() => setIsCreateUserModalOpen(true)}>
                <p className={styles.newUserButtonText}>New User</p>
              </Button>
            </>
          ) : (
            <Badge
              badgeContent={getActiveFiltersCount()}
              invisible={getActiveFiltersCount() === 0}
              classes={{ badge: styles.filtersButtonBadge }}>
              <Button
                variant="contained"
                endIcon={<Sort />}
                className={styles.filtersButton}
                onClick={() => setIsFiltersDialogOpen(true)}>
                <p className={styles.buttonText}>Filters</p>
              </Button>
            </Badge>
          )}
        </div>
      </div>
      {isEmpty(currentTab === 'users' ? users : historyRecords) || hasFetchError ? (
        <div className={styles.noExperimentsContainer}>
          {hasFetchError ? <img src={FetchError} /> : <img src={EmptyBox} />}
          <p className={styles.noExperimentsHeader}>
            {hasFetchError
              ? 'Error Fetching List'
              : `No ${currentTab === 'users' ? 'users' : 'results'} found for your search`}
          </p>
          <p className={styles.noExperimentsText}>
            {hasFetchError
              ? `Unable to load the ${currentTab === 'users' ? 'users' : 'history'} list. Please try again later`
              : 'Try adjusting your search criteria '}
          </p>
        </div>
      ) : (
        <>
          {currentTab === 'users' ? (
            <div className={styles.usersContainer}>{users.map(user => usersCard(user))}</div>
          ) : (
            <div className={styles.historyContainer}>{historyRecords.map(history => historyCard(history))}</div>
          )}
          <div className={styles.footerContainer}>
            <p className={styles.experimentsCountText}>
              Showing {currentTab === 'users' ? getUsersCount.start : getHistoryCount.start} to{' '}
              {currentTab === 'users' ? getUsersCount.end : getHistoryCount.end} of{' '}
              {currentTab === 'users' ? getUsersCount.total : getHistoryCount.total} items
            </p>

            <div className={styles.paginationContainer}>
              <IconButton
                className={styles.paginationButton}
                disabled={currentTab === 'users' ? page === 1 : historyPage === 1}
                onClick={() => {
                  currentTab === 'users'
                    ? fetchUsers(1, {
                        role: selectedRoles,
                        search: debouncedSearch,
                      })
                    : fetchHistory(1, historyFilters)
                }}>
                <KeyboardDoubleArrowLeft />
              </IconButton>
              <IconButton
                className={styles.paginationButton}
                disabled={currentTab === 'users' ? page === 1 : historyPage === 1}
                onClick={() => {
                  currentTab === 'users'
                    ? fetchUsers(page - 1, {
                        role: selectedRoles,
                        search: debouncedSearch,
                      })
                    : fetchHistory(historyPage - 1, historyFilters)
                }}>
                <KeyboardArrowLeft />
              </IconButton>
              <p
                className={
                  styles.paginationPageText
                }>{`${currentTab === 'users' ? page : historyPage} - ${currentTab === 'users' ? getLastPage() : getHistoryLastPage()}`}</p>
              <IconButton
                className={styles.paginationButton}
                disabled={currentTab === 'users' ? page === getLastPage() : historyPage === getHistoryLastPage()}
                onClick={() => {
                  currentTab === 'users'
                    ? fetchUsers(page + 1, {
                        role: selectedRoles,
                        search: debouncedSearch,
                      })
                    : fetchHistory(historyPage + 1, historyFilters)
                }}>
                <KeyboardArrowRight />
              </IconButton>
              <IconButton
                className={styles.paginationButton}
                disabled={currentTab === 'users' ? page === getLastPage() : historyPage === getHistoryLastPage()}
                onClick={() => {
                  currentTab === 'users'
                    ? fetchUsers(getLastPage(), {
                        role: selectedRoles,
                        search: debouncedSearch,
                      })
                    : fetchHistory(getHistoryLastPage(), historyFilters)
                }}>
                <KeyboardDoubleArrowRight />
              </IconButton>
            </div>
          </div>
        </>
      )}
      <CustomDialog
        open={isDeleteModalOpen}
        setClose={() => {
          setIsDeleteModalOpen(false)
          setSelectedUser(null)
        }}
        title="Delete conversation"
        primaryAction={() => handleDeleteUser()}
        primaryActionButtonText="Delete"
        secondaryAction={() => {
          setIsDeleteModalOpen(false)
          setSelectedUser(null)
        }}
        secondaryActionButtonText="Cancel">
        <Stack>
          <Typography className={styles.deleteDialogContentText}>
            This action is <b>permanent</b> and cannot be reversed once confirmed. Please make sure you reviewed all
            before proceeding.
          </Typography>
        </Stack>
      </CustomDialog>
      <CreateUserModal
        open={isCreateUserModalOpen}
        setClose={() => {
          setIsCreateUserModalOpen(false)
          setSelectedUser(null)
        }}
        primaryAction={isEditingUser ? handleUserChange : handleCreateUser}
        ouOptions={ouOptions}
        errors={errors}
        resetErrors={(field?: string) => {
          if (field) {
            setErrors(prev => prev.filter(error => error.field !== field))
          } else {
            setErrors([])
          }
        }}
        selectedUser={selectedUser}
      />
      <FiltersDialog
        open={isFiltersDialogOpen}
        primaryAction={async (filters: UserHistoryParams) => {
          setIsFiltersDialogOpen(false)
          fetchHistory(1, filters)
        }}
        primaryActionButtonText="Apply Filters"
        secondaryAction={() => {
          setIsFiltersDialogOpen(false)
        }}
        secondaryActionButtonText="Cancel"
        title="Filters"
        filters={historyFilters}
        filterOptions={filterOptions}
      />
    </div>
  )
}

export default UsersRolesAndPermissionsManagement
